import React from 'react';
import { useLayout } from '@webV2/providers/LayoutProvider/context';
import { TacticIcon } from '@cmp-src/icons/TacticLogo/TacticIcon';
import { Plus } from '@cmp-src/icons/TacticIcons/Plus';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider
} from '@v2/components/ui/tooltip';
import { useRouterPush } from '@webV2/helpers/router/useRouterPush';
import { cn } from '@v2/lib/utils';
import { WebRoutes } from '@webV2/defines/routes';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@v2/components/ui/dropdown-menu';
import { useTranslation } from 'next-i18next';
import { NavUser } from '@/components/layouts/v2/layout/nav-user';
import { useAuthenticated } from '@gettactic/hooks';
import { useReserve } from '@webV2/providers/ReserveProvider/context';
import { ImpersonationButton } from './ImpersonationButton';
import { useBreakpoint } from '@gettactic/helpers/src/hooks/useBreakpoint';
import { authenticatedUserIsInMoreOrganizations } from '@gettactic/helpers/src/features/authenticatedUserIsInMoreOrganizations';
import { OrganizationSwitcher } from '@v2/components/organizations/OrganizationSwitcher/OrganizationSwitcher';
import { Link } from '@v2/components/ui/Link';
import { useDisclosure } from '@gettactic/helpers';
import { Permissions } from '@gettactic/api';

export const roundButtonClassNames =
  'flex h-[36px] w-[36px] items-center justify-center rounded-full';
const classes = {
  Wrapper:
    'justify-between bg-white w-full md:bg-transparent fixed left-0 right-0 z-sticky md:static md:h-full px-4 py-2 md:py-5 md:overflow-hidden flex md:flex-col',
  Logo: 'w-6 md:w-auto block text-primary-bg relative',
  SectionsContainer:
    'border-t border-t-neutral-200 md:border-t-0 md:shadow-none fixed py-2 md:py-0 bg-white md:bg-transparent flex justify-center md:justify-start md:flex-col md:static z-sticky bottom-0 left-0 right-0 mt-6 gap-6 md:flex-1 overflow-hidden',
  LogoIcon: 'size-8.5 cursor-pointer',
  MenuItem:
    'size-9 overflow-hidden p-1.5 transition-colors hover:text-v2-sidebar-icons-selected hover:cursor-pointer',
  MenuItemActive: 'text-v2-sidebar-icons-selected',
  MenuItemNotActive: 'text-v2-sidebar-icons',
  Icon: 'size-6',
  FooterContainer:
    'flex flex-row md:flex-col gap-5 md:mt-5 items-center justify-center',
  PlusButton: `${roundButtonClassNames} hidden md:flex bg-primary text-primary-text`,
  PlusIcon: 'size-[18px]',
  ExtraButton: 'p-1.5',
  ExtraIcon: 'size-6'
};

const isSectionEnabled = (
  enabled: boolean | ((permissions: Permissions) => boolean),
  permissions: Permissions
): boolean => {
  if (typeof enabled === 'function') {
    return enabled(permissions);
  }
  return enabled;
};

export const MainSidebar: React.FC = () => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const organizationSwitcherDisclosure = useDisclosure();
  const { isBreakpoint } = useBreakpoint();
  const { sidebarSize, navSections, selectedFloor } = useLayout();
  const { openReserveModal } = useReserve();
  const routerPush = useRouterPush();
  const { t } = useTranslation('common');

  const isMd = isBreakpoint('md');

  const handleReserveRoom = (e: React.MouseEvent) => {
    // we gotta wait for the dropdown to close before opening the modal
    // otherwise the modal will open and close immediately

    setTimeout(() => {
      openReserveModal({
        resourceType: 'meeting_room'
      });
    }, 200);
  };

  const handleReserveDesk = (e: React.MouseEvent) => {
    // we gotta wait for the dropdown to close before opening the modal
    // otherwise the modal will open and close immediately

    setTimeout(() => {
      openReserveModal({
        resourceType: 'desk'
      });
    }, 200);
  };

  const inMoreOrganizations =
    authenticatedUserIsInMoreOrganizations(authenticatedUser);

  return (
    <TooltipProvider delayDuration={0}>
      <div
        className={classes.Wrapper}
        style={isMd ? { width: sidebarSize } : undefined}
      >
        <Link
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (inMoreOrganizations) {
              organizationSwitcherDisclosure.open();
              return;
            }
            routerPush(WebRoutes.dashboard);
          }}
          href={WebRoutes.dashboard}
          className={classes.Logo}
        >
          <span>
            <TacticIcon className={classes.LogoIcon} />
          </span>
        </Link>
        <div className={classes.SectionsContainer}>
          {navSections.map((section) => {
            if (!isSectionEnabled(section.enabled, authenticatedUser.permissions)) return;
            return (
              <div
                className={cn(
                  classes.MenuItem,
                  section.active
                    ? classes.MenuItemActive
                    : classes.MenuItemNotActive
                )}
                key={section.id}
              >
                <Tooltip>
                  <TooltipTrigger>
                    <Link
                      href={
                        section.url +
                        (section.appendFloorId && selectedFloor
                          ? `?floorId=${selectedFloor.id}`
                          : '')
                      }
                    >
                      {React.createElement(section.icon, {
                        className: classes.Icon,
                        isActive: section.active
                      })}
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side={isMd ? 'right' : 'top'} align="center">
                    {t(`navItems.${section.id}`)}
                  </TooltipContent>
                </Tooltip>
              </div>
            );
          })}
        </div>
        <div className={classes.FooterContainer}>
          <ImpersonationButton />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={classes.PlusButton}>
                <Plus className={classes.PlusIcon} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="right">
              <DropdownMenuItem onClick={handleReserveRoom}>
                {t('navActions.reserveRoom')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleReserveDesk}>
                {t('navActions.reserveDesk')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <NavUser
            user={{
              name: authenticatedUser.user?.name || '',
              email: authenticatedUser.user?.email || '',
              avatar: authenticatedUser.user?.profile_photo_url || ''
            }}
          />
        </div>
      </div>
      {inMoreOrganizations && (
        <OrganizationSwitcher
          isOpen={organizationSwitcherDisclosure.isOpen}
          onOpenChange={organizationSwitcherDisclosure.onOpenChange}
        />
      )}
    </TooltipProvider>
  );
};
